/**
 * FormBuilderPreview - Preview mode.
 *
 * Renders the form as it would appear to an end user (read-only, no editing).
 */

import { cn } from '@/shared/lib/cn';
import { Checkbox, Input, Label, Textarea } from '@/shared/ui';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui';
import { formatControlType } from './builder-utils';
import type { FormControl, FormDefinition } from './types';

function PreviewControl({ control }: { control: FormControl }) {
  const spanClass = (() => {
    switch (control.control_span_length) {
      case '6':
        return 'col-span-6';
      case '4':
        return 'col-span-4';
      case '3':
        return 'col-span-3';
      default:
        return 'col-span-12';
    }
  })();

  const renderInput = () => {
    switch (control.control_type) {
      case 'text':
      case 'email':
      case 'number':
      case 'tel':
      case 'url':
        return (
          <Input
            type={
              control.control_type === 'email'
                ? 'email'
                : control.control_type === 'number'
                  ? 'number'
                  : 'text'
            }
            placeholder={control.control_description || `Enter ${control.control_name}`}
            disabled
            className="h-9 text-sm bg-zinc-50"
          />
        );

      case 'textarea':
      case 'long_textarea':
        return (
          <Textarea
            placeholder={control.control_description || `Enter ${control.control_name}`}
            disabled
            rows={3}
            className="text-sm bg-zinc-50"
          />
        );

      case 'date':
        return <Input type="date" disabled className="h-9 text-sm bg-zinc-50" />;

      case 'time':
        return <Input type="time" disabled className="h-9 text-sm bg-zinc-50" />;

      case 'radio':
        return (
          <div className="space-y-2">
            {(control.options || []).map((opt, idx) => (
              <label key={idx} className="flex items-center gap-2 text-sm text-zinc-700">
                <input type="radio" name={control.q_id} disabled className="accent-[#E84A2B]" />
                {opt.option || `Option ${idx + 1}`}
              </label>
            ))}
            {(control.options || []).length === 0 && (
              <p className="text-xs text-zinc-400 italic">No options defined</p>
            )}
          </div>
        );

      case 'checkbox':
        return (
          <div className="space-y-2">
            {(control.options || []).map((opt, idx) => (
              <label key={idx} className="flex items-center gap-2 text-sm text-zinc-700">
                <Checkbox disabled />
                {opt.option || `Option ${idx + 1}`}
              </label>
            ))}
            {(control.options || []).length === 0 && (
              <p className="text-xs text-zinc-400 italic">No options defined</p>
            )}
          </div>
        );

      case 'option':
        return (
          <Select disabled>
            <SelectTrigger className="h-9 text-sm bg-zinc-50">
              <SelectValue placeholder="Select an option" />
            </SelectTrigger>
            <SelectContent>
              {(control.options || []).map((opt, idx) => (
                <SelectItem key={idx} value={opt.option || `opt-${idx}`}>
                  {opt.option || `Option ${idx + 1}`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );

      case 'switch':
        return (
          <div className="flex items-center gap-2">
            <div className="w-10 h-5 rounded-full bg-zinc-200 relative">
              <div className="absolute left-0.5 top-0.5 w-4 h-4 rounded-full bg-white shadow" />
            </div>
            <span className="text-xs text-zinc-500">Off</span>
          </div>
        );

      case 's3_image':
        return (
          <div className="border-2 border-dashed border-zinc-300 rounded-lg p-6 text-center">
            <p className="text-xs text-zinc-500">Image upload area</p>
          </div>
        );

      case 'signature':
        return (
          <div className="border-2 border-dashed border-zinc-300 rounded-lg p-8 text-center">
            <p className="text-xs text-zinc-500">Signature capture area</p>
          </div>
        );

      case 'label':
        return (
          <p className="text-sm text-zinc-600">
            {control.control_description || control.control_name}
          </p>
        );

      default:
        return (
          <Input
            placeholder={formatControlType(control.control_type)}
            disabled
            className="h-9 text-sm bg-zinc-50"
          />
        );
    }
  };

  return (
    <div className={cn(spanClass, 'space-y-1.5')}>
      {control.control_type !== 'label' && (
        <Label className="text-sm font-medium text-zinc-700">
          {control.control_name || 'Untitled field'}
          {control.required && <span className="text-red-500 ml-0.5">*</span>}
        </Label>
      )}
      {control.help_text && <p className="text-xs text-zinc-500">{control.help_text}</p>}
      {renderInput()}
    </div>
  );
}

interface FormBuilderPreviewProps {
  form: FormDefinition;
}

export default function FormBuilderPreview({ form }: FormBuilderPreviewProps) {
  return (
    <div className="border rounded-lg bg-white max-w-3xl mx-auto">
      {/* Form header */}
      <div className="px-6 py-5 border-b">
        <h2 className="text-xl font-bold text-zinc-900">{form.name || 'Untitled Form'}</h2>
        {form.description && <p className="text-sm text-zinc-500 mt-1">{form.description}</p>}
      </div>

      {/* Sections */}
      <div className="px-6 py-4 space-y-6">
        {form.sections.length === 0 && (
          <p className="text-sm text-zinc-400 text-center py-8">
            No sections to preview. Add sections and fields in the builder.
          </p>
        )}

        {form.sections.map((section, sectionIndex) => (
          <div key={section.id ?? sectionIndex} className="space-y-4">
            <div>
              <h3 className="text-base font-semibold text-zinc-800">
                {section.section_name || `Section ${sectionIndex + 1}`}
              </h3>
              {section.section_description && (
                <p className="text-xs text-zinc-500 mt-0.5">{section.section_description}</p>
              )}
            </div>

            <div className="grid grid-cols-12 gap-4">
              {section.controls.map((control, controlIndex) => (
                <PreviewControl key={control.q_id ?? controlIndex} control={control} />
              ))}
            </div>

            {sectionIndex < form.sections.length - 1 && <hr className="border-zinc-200" />}
          </div>
        ))}
      </div>
    </div>
  );
}
